import { LongyLogo } from '@longy/web-components'
import { Link } from '@tanstack/react-router'
import { Menu, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCmsPagesMenu } from '../../hooks/useCmsPagesMenu'
import { useCmsSiteHeader } from '../../hooks/useCmsSiteHeader'
import { useWebContent } from '../../hooks/useWebContent'
import { buildHeaderNav, preferCmsLabel } from '../../utils/headerNav'
import { HeaderAccountMenu } from '../auth/HeaderAccountMenu'
import { LanguageSwitcher } from '../LanguageSwitcher'
import { BookingCtaButton } from './BookingCtaButton'
import { BookingTypeModal } from './BookingTypeModal'
import { HeaderDesktopNav } from './HeaderDesktopNav'
import { HeaderMobileMenu } from './HeaderMobileMenu'

export const Header = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [bookingModalOpen, setBookingModalOpen] = useState(false)
    const { getText } = useWebContent('HEADER')
    const { t } = useTranslation()

    const cmsNavLinks = useCmsSiteHeader()
    const cmsPageLinks = useCmsPagesMenu()

    /*
     * Five of these labels have been editable in admin since before the grouping existed, so they
     * keep answering to the cms — with the product's own wording behind them, because a blanked
     * field used to render a nameless gap. The rest are new and have no cms key, which is what puts
     * `Szakembereknek` in a translation file instead of the hardcoded Hungarian it used to be.
     */
    const navEntries = buildHeaderNav({
        labels: {
            services: t('frontend.header.services'),
            camps: preferCmsLabel(getText('navCamps'), t('frontend.header.camps')),
            programs: preferCmsLabel(getText('navPrograms'), t('frontend.header.programs')),
            packages: t('frontend.header.packages'),
            videoConsultations: t('frontend.header.videoConsultations'),
            careExaminations: t('frontend.header.careExaminations'),
            webinars: t('frontend.header.webinars'),
            knowledgeBase: preferCmsLabel(
                getText('navKnowledge'),
                t('frontend.header.knowledgeBase'),
            ),
            about: preferCmsLabel(getText('navAbout'), t('frontend.header.about')),
            forExperts: t('frontend.header.forExperts'),
            contact: preferCmsLabel(getText('navContact'), t('frontend.header.contact')),
            pages: t('frontend.cms.pagesMenu'),
        },
        cmsLinks: cmsNavLinks,
        pageLinks: cmsPageLinks,
    })

    // The menu is a sibling of the dialog rather than its trigger, so it has to be dismissed by
    // hand — otherwise the whole sheet sits behind the modal and is still there when it closes.
    const openBookingModalFromMobile = () => {
        setMobileMenuOpen(false)
        setBookingModalOpen(true)
    }

    return (
        <header className="sticky top-0 z-50 w-full border-b border-slate-100 bg-white/90 backdrop-blur-md">
            <div className="mx-auto flex h-20 max-w-[1280px] items-center justify-between px-6">
                <div className="flex items-center gap-12">
                    <Link to="/" className="flex items-center">
                        <LongyLogo className="h-24" />
                    </Link>
                    <HeaderDesktopNav entries={navEntries} />
                </div>
                <div className="flex items-center gap-4">
                    <div className="hidden items-center gap-3 lg:flex">
                        <BookingCtaButton onClick={() => setBookingModalOpen(true)} />
                        <HeaderAccountMenu />
                        <LanguageSwitcher />
                    </div>
                    <button
                        className="lg:hidden"
                        aria-label={
                            mobileMenuOpen
                                ? t('frontend.header.closeMenu')
                                : t('frontend.header.openMenu')
                        }
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    >
                        {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </button>
                </div>
            </div>

            {mobileMenuOpen && (
                <HeaderMobileMenu
                    entries={navEntries}
                    onNavigate={() => setMobileMenuOpen(false)}
                    onBookingCtaClick={openBookingModalFromMobile}
                />
            )}

            <BookingTypeModal open={bookingModalOpen} onOpenChange={setBookingModalOpen} />
        </header>
    )
}
